// src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Auth } from "aws-amplify";
import { CognitoUser, CognitoUserSession } from "amazon-cognito-identity-js";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContextType, AuthError, AuthFlow, HeadersConfig } from "../types/auth";
import { ApiUserData, ApiCompanyData, Regions, getConfigForRegion } from "../config";

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const navigate = useNavigate();
    const IS_TEST_ENV = process.env.REACT_APP_ENV === "test";

    // Authentication states
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [bearerToken, setBearerToken] = useState("");
    const [flow, setFlow] = useState<AuthFlow>("SIGNIN");
    const [error, setError] = useState<AuthError | null>(null);
    const [tempUser, setTempUser] = useState<CognitoUser | null>(null);
    const [totp, setTotp] = useState("");
    const [mfaEmail, setMfaEmail] = useState("");

    // Data states
    const [users, setUsers] = useState<ApiUserData[]>([]);
    const [companies, setCompanies] = useState<ApiCompanyData[]>([]);
    const [usersValid, setUsersValid] = useState(false);
    const [companiesValid, setCompaniesValid] = useState(false);

    const headersWithAuth: HeadersConfig | undefined = useMemo(() => {
        if (!bearerToken) return undefined;
        return { headers: { Authorization: `Bearer ${bearerToken}` } };
    }, [bearerToken]);

    const resetError = () => setError(null);

    const scheduleTokenRefresh = useCallback(
        (
            session: CognitoUserSession,
            handleAuthentication: (session: CognitoUserSession) => void,
            refreshTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>
        ) => {
            const expiration = session.getAccessToken().getExpiration(); // expiration in seconds since epoch
            const now = Math.floor(Date.now() / 1000);
            const timeUntilExpiry = expiration - now;

            const refreshDelay = (timeUntilExpiry - 60) * 1000;

            if (refreshDelay <= 0) {
                refreshToken();
            } else {
                if (refreshTimeoutRef.current) {
                    clearTimeout(refreshTimeoutRef.current);
                }
                refreshTimeoutRef.current = setTimeout(refreshToken, refreshDelay);
            }

            async function refreshToken() {
                try {
                    const refreshedSession = await Auth.currentSession();
                    handleAuthentication(refreshedSession);
                    scheduleTokenRefresh(refreshedSession, handleAuthentication, refreshTimeoutRef);
                } catch (error) {
                    console.error("Token refresh failed:", error);
                }
            }
        },
        []
    );

    const resetApp = () => {
        setIsAuthenticated(false);
        setIsAuthenticating(false);
        setBearerToken("");
        setFlow("SIGNIN");
        setUsersValid(false);
        setCompaniesValid(false);
    };

    const handleAuthentication = useCallback(
        (userSession: CognitoUserSession | null, error?: AuthError) => {
            if (userSession) {
                console.log("Setting authenticated session");
                setIsAuthenticated(true);
                setBearerToken(userSession.getIdToken().getJwtToken());
                setError(null);
                // **Schedule token refresh when authentication is successful**
                scheduleTokenRefresh(userSession, handleAuthentication, refreshTimeoutRef);
            } else {
                // **Clear any scheduled refresh if the session is lost or on error**
                if (refreshTimeoutRef.current) {
                    clearTimeout(refreshTimeoutRef.current);
                    refreshTimeoutRef.current = null;
                }
                setIsAuthenticated(false);
                setBearerToken("");
                setFlow("SIGNIN");
                setUsersValid(false);
                setCompaniesValid(false);
                if (error?.message && error.message !== "No current user") {
                    console.log("Authentication error:", error.message);
                    setError(error);
                } else {
                    setError(null);
                }
            }
            setIsAuthenticating(false);
        },
        [scheduleTokenRefresh]
    );

    // Check authentication status on mount
    useEffect(() => {
        let isSubscribed = true;

        const checkAuth = async () => {
            try {
                const session = await Auth.currentSession();
                if (isSubscribed) {
                    handleAuthentication(session);
                }
            } catch (error) {
                if (isSubscribed) {
                    // Only handle as error if it's not the "No current user" case
                    if (error instanceof Error && error.message !== "No current user") {
                        handleAuthentication(null, {
                            message: error.message
                        });
                    } else {
                        // Just reset auth state without error for normal non-authenticated cases
                        handleAuthentication(null);
                    }
                }
            }
        };

        checkAuth();

        return () => {
            isSubscribed = false;
        };
    }, [handleAuthentication]);

    const signIn = async (email: string, password: string): Promise<void> => {
        try {
            setIsLoading(true);
            setError(null);

            const user = await Auth.signIn(email, password);

            if (!user) {
                throw new Error("No user returned from sign in");
            }

            setTempUser(user);
            setMfaEmail(email);

            if (IS_TEST_ENV) {
                const session = user.getSignInUserSession();
                if (session) {
                    handleAuthentication(session);
                    navigate("/home");
                }
                return;
            }

            const challenge = user.challengeName;
            switch (challenge) {
                case "MFA_SETUP": {
                    const totpCode = await Auth.setupTOTP(user);
                    setTotp(totpCode);
                    setFlow("MFA_SETUP");
                    break;
                }
                case "SOFTWARE_TOKEN_MFA": {
                    setFlow("MFA");
                    break;
                }
                case "NEW_PASSWORD_REQUIRED": {
                    setFlow("FIRST_TIME");
                    break;
                }
                case undefined: {
                    const session = user.getSignInUserSession();
                    if (session) {
                        handleAuthentication(session);
                        navigate("/home");
                    }
                    break;
                }
                default: {
                    throw new Error(`Unexpected challenge: ${challenge}`);
                }
            }
        } catch (err: any) {
            console.error("SignIn Error:", err);
            setTempUser(null);

            let errorMessage = "An error occurred during sign in.";

            switch (err.code) {
                case "UserNotFoundException":
                case "NotAuthorizedException":
                    errorMessage = "Incorrect username or password.";
                    break;
                case "UserNotConfirmedException":
                    errorMessage = "Please confirm your account.";
                    break;
                case "NetworkError":
                    errorMessage = "Network error occurred. Please check your connection.";
                    break;
                default:
                    errorMessage = err.message || errorMessage;
                    break;
            }

            setError({
                code: err.code,
                message: errorMessage
            });
            setFlow("SIGNIN");
        } finally {
            setIsLoading(false);
        }
    };

    const submitMfa = async (code: string): Promise<void> => {
        try {
            setIsLoading(true);
            setError(null);

            if (!tempUser) {
                throw new Error("No user found for MFA verification");
            }

            await Auth.confirmSignIn(tempUser, code, "SOFTWARE_TOKEN_MFA");
            const session = tempUser.getSignInUserSession();
            if (session) {
                handleAuthentication(session);
                navigate("/home");
            }
        } catch (err: any) {
            console.error("MFA Error:", err);

            let errorMessage = "Failed to verify MFA code";

            if (err.code === "CodeMismatchException") {
                errorMessage = "Invalid verification code.";
            }

            setError({
                code: err.code,
                message: errorMessage
            });
        } finally {
            setIsLoading(false);
        }
    };

    const signOut = async (): Promise<void> => {
        try {
            await Auth.signOut();
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
            resetApp();
            navigate("/");
        } catch (error) {
            setError({
                message: error instanceof Error ? error.message : "An unknown error occurred"
            });
        }
    };

    // MFA setup implementation
    const setupMfa = async (code: string): Promise<void> => {
        try {
            setIsLoading(true);
            if (!tempUser) {
                throw new Error("No user found for MFA setup");
            }

            await Auth.verifyTotpToken(tempUser, code);
            const session = tempUser.getSignInUserSession();
            if (session) {
                handleAuthentication(session);
                navigate("/home");
            }
        } catch (error) {
            setError({
                message: error instanceof Error ? error.message : "Failed to setup MFA"
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Forgot password implementation
    const forgotPassword = async (email: string): Promise<void> => {
        try {
            setIsLoading(true);
            await Auth.forgotPassword(email);
            setFlow("FORGOT_PASSWORD_SUBMIT");
        } catch (error) {
            setError({
                message: error instanceof Error ? error.message : "Failed to process forgot password request"
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Submit new password after reset
    const forgotPasswordSubmit = async (email: string, code: string, newPassword: string): Promise<void> => {
        try {
            setIsLoading(true);
            await Auth.forgotPasswordSubmit(email, code, newPassword);
            setFlow("SIGNIN");
        } catch (error) {
            setError({
                message: error instanceof Error ? error.message : "Failed to reset password"
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Complete new password challenge
    const completeNewPassword = async (email: string, tempPassword: string, newPassword: string): Promise<void> => {
        try {
            setIsLoading(true);
            if (!tempUser) {
                const user = await Auth.signIn(email, tempPassword);
                setTempUser(user);
            }

            if (!tempUser) {
                throw new Error("Failed to get user for password completion");
            }

            const loggedUser = await Auth.completeNewPassword(tempUser, newPassword);
            const session = loggedUser.getSignInUserSession();
            if (session) {
                handleAuthentication(session);
                navigate("/home");
            }
        } catch (error) {
            setError({
                message: error instanceof Error ? error.message : "Failed to complete password setup"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const getUsersData = useCallback(async (): Promise<void> => {
        if (!headersWithAuth) return;

        try {
            const usersPromises = Object.values(Regions).map(async (region) => {
                const regionConfig = getConfigForRegion(region);
                try {
                    console.log(`Fetching users from ${region.displayName}`);
                    const response = await axios.post<ApiUserData[]>(regionConfig.api.USERS, {}, headersWithAuth);

                    if (response?.status >= 400) {
                        console.error(`[Users ${region.displayName}] Failed with status:`, response.status);
                        return [];
                    }

                    return response.data.map((user) => ({
                        ...user,
                        region: region.displayName
                    }));
                } catch (error) {
                    console.error(`[Users ${region.displayName}] Error:`, error);
                    return [];
                }
            });

            const results = await Promise.all(usersPromises);
            const allUsers = results.flat();

            console.log("All users fetched:", allUsers.length);
            setUsers(allUsers);
            setUsersValid(true);
        } catch (error) {
            console.error("[Users] Error:", error);
        }
    }, [headersWithAuth]);

    const getCompaniesData = useCallback(async (): Promise<void> => {
        if (!headersWithAuth) return;

        try {
            const companiesPromises = Object.values(Regions).map(async (region) => {
                const regionConfig = getConfigForRegion(region);
                try {
                    const response = await axios.post<ApiCompanyData[]>(
                        regionConfig.api.COMPANIES,
                        {},
                        headersWithAuth
                    );

                    if (response?.status >= 400) {
                        console.log(
                            `[Companies ${region.displayName}] API request failed with status: ${response?.status}`
                        );
                        return [];
                    }

                    return response.data.map((company) => ({
                        ...company,
                        region: region.displayName
                    }));
                } catch (error) {
                    console.log(`[Companies ${region.displayName}] Error fetching data:`, error);
                    return [];
                }
            });

            const results = await Promise.all(companiesPromises);
            const allCompanies = results.flat();

            if (allCompanies.length > 0) {
                setCompanies(allCompanies);
                setCompaniesValid(true);
            }
        } catch (error) {
            console.log("[Companies] Error fetching data:", error);
        }
    }, [headersWithAuth]);

    // Effects for data fetching
    useEffect(() => {
        if (!isAuthenticated || !headersWithAuth || usersValid) return;

        const fetchData = async () => {
            try {
                await getUsersData();
            } catch (error) {
                console.error("[Users] Error fetching:", error);
            } finally {
                setUsersValid(true);
            }
        };

        fetchData();
    }, [isAuthenticated, headersWithAuth, usersValid, getUsersData]);

    useEffect(() => {
        if (!isAuthenticated || !headersWithAuth || companiesValid) return;

        const fetchCompanies = async () => {
            try {
                await getCompaniesData();
            } catch (error) {
                console.error("[Companies] Error fetching:", error);
            } finally {
                setCompaniesValid(true);
            }
        };

        fetchCompanies();
    }, [isAuthenticated, headersWithAuth, companiesValid, getCompaniesData]);

    const value: AuthContextType = {
        // Auth states
        isAuthenticated,
        isAuthenticating,
        isLoading,
        bearerToken,
        headersWithAuth,
        flow,
        error,
        totp,
        mfaEmail,

        // Data states
        users,
        companies,
        usersValid,
        companiesValid,

        // Auth methods
        setFlow,
        signIn,
        signOut,
        forgotPassword,
        forgotPasswordSubmit,
        completeNewPassword,
        submitMfa,
        setupMfa,
        resetError,
        handleAuthentication,

        // Data methods
        refreshUsers: getUsersData,
        refreshCompanies: getCompaniesData
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
